import React, { useEffect, useState } from 'react';
import { 
  Container, 
  Title, 
  Paper, 
  Text, 
  Stack, 
  Group, 
  Badge,
  Loader,
  Center,
  Grid,
  Card
} from '@mantine/core';
import api from '../services/api';
import Layout from '../components/Layout';

interface MenuQueryLog {
  id: string;
  query: string;
  response: string;
  classification: string;
  createdAt: string;
  inputTokens: number | null;
  outputTokens: number | null;
  totalTokens: number | null;
  geminiModel: string | null;
  querySetup?: {
    modelName?: string;
    configurationId?: string;
    systemInstruction?: string;
    promptTemplate?: string;
  } | null;
  menu: {
    id: string;
    name: string;
    restaurant: {
      name: string;
    };
  };
  user: {
    name: string;
  } | null;
}

const QueryLogs: React.FC = () => {
  const [logs, setLogs] = useState<MenuQueryLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchLogs = async () => {
      try {
        // api service appends /api to the path
        const response = await api.get('/query-logs');
        setLogs(response.data);
      } catch (err: any) {
        setError(err.response?.data?.message || 'Failed to fetch query logs');
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, []);

  // Calculate summary statistics
  const summaryStats = logs.reduce((stats, log) => {
    if (log.inputTokens) stats.totalInputTokens += log.inputTokens;
    if (log.outputTokens) stats.totalOutputTokens += log.outputTokens;
    if (log.totalTokens) stats.totalTokens += log.totalTokens;
    if (log.geminiModel) {
      stats.modelUsage[log.geminiModel] = (stats.modelUsage[log.geminiModel] || 0) + 1;
    }
    return stats;
  }, {
    totalInputTokens: 0,
    totalOutputTokens: 0,
    totalTokens: 0,
    modelUsage: {} as Record<string, number>
  });

  if (loading) {
    return (
      <Layout>
        <Center h={400}>
          <Loader size="xl" />
        </Center>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <Container>
          <Text c="red">{error}</Text>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container>
        <Title order={1} mb="xl">Query Logs</Title>
        
        {/* Summary Statistics */}
        {logs.length > 0 && (
          <Paper p="md" mb="xl" bg="gray.0" withBorder>
            <Text fw={600} mb="md">Token Usage Summary ({logs.length} queries)</Text>
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Card p="xs" withBorder>
                  <Text size="sm" c="dimmed">Total Input Tokens</Text>
                  <Text fw={600} size="lg" c="green">
                    {summaryStats.totalInputTokens.toLocaleString()}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Card p="xs" withBorder>
                  <Text size="sm" c="dimmed">Total Output Tokens</Text>
                  <Text fw={600} size="lg" c="orange">
                    {summaryStats.totalOutputTokens.toLocaleString()}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Card p="xs" withBorder>
                  <Text size="sm" c="dimmed">Total Tokens</Text>
                  <Text fw={600} size="lg" c="violet">
                    {summaryStats.totalTokens.toLocaleString()}
                  </Text>
                </Card>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Card p="xs" withBorder>
                  <Text size="sm" c="dimmed">Average per Query</Text>
                  <Text fw={600} size="lg" c="blue">
                    {logs.length > 0 ? Math.round(summaryStats.totalTokens / logs.length).toLocaleString() : 0}
                  </Text>
                </Card>
              </Grid.Col>
            </Grid>
            
            {/* Model Usage */}
            {Object.keys(summaryStats.modelUsage).length > 0 && (
              <Stack gap="xs" mt="md">
                <Text size="sm" fw={500}>Model Usage:</Text>
                <Group gap="xs">
                  {Object.entries(summaryStats.modelUsage).map(([model, count]) => (
                    <Badge key={model} variant="light" color="green">
                      {model}: {count} queries
                    </Badge>
                  ))}
                </Group>
              </Stack>
            )}
          </Paper>
        )}
        
        <Stack gap="md">
          {logs.map((log) => (
            <Paper key={log.id} p="md" withBorder>
              <Stack gap="xs">
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">
                    {new Date(log.createdAt).toLocaleString()}
                  </Text>
                  <Badge>{log.classification}</Badge>
                </Group>
                
                {/* Token Information */}
                {(log.inputTokens || log.outputTokens || log.totalTokens || log.geminiModel) && (
                    <Group gap="md" wrap="wrap">
                      {log.geminiModel && (
                        <Badge variant="light" color="green">
                          Model: {log.geminiModel}
                        </Badge>
                      )}
                      {log.inputTokens && (
                        <Badge variant="light" color="green">
                          Input: {log.inputTokens.toLocaleString()} tokens
                        </Badge>
                      )}
                      {log.outputTokens && (
                        <Badge variant="light" color="orange">
                          Output: {log.outputTokens.toLocaleString()} tokens
                        </Badge>
                      )}
                      {log.totalTokens && (
                        <Badge variant="filled" color="violet">
                          Total: {log.totalTokens.toLocaleString()} tokens
                        </Badge>
                      )}
                    </Group>
                )}

                {/* Query Setup Information */}
                {log.querySetup && (
                  <Group gap="md" wrap="wrap">
                    {log.querySetup.modelName && (
                      <Badge variant="light" color="indigo">
                        Config Model: {log.querySetup.modelName}
                      </Badge>
                    )}
                    {log.querySetup.configurationId && (
                      <Badge variant="light" color="teal">
                        Config: {log.querySetup.configurationId === 'japan-restaurant' ? 'Japan Restaurant' : 
                                log.querySetup.configurationId === 'american-restaurant' ? 'American Restaurant' : 
                                log.querySetup.configurationId}
                      </Badge>
                    )}
                    {log.querySetup.systemInstruction && (
                      <Badge variant="light" color="grape">
                        System: {log.querySetup.systemInstruction}
                      </Badge>
                    )}
                    {log.querySetup.promptTemplate && (
                      <Badge variant="light" color="pink">
                        Prompt: {log.querySetup.promptTemplate}
                      </Badge>
                    )}
                  </Group>
                )}
                
                <Text fw={500}>Query:</Text>
                <Text>{log.query}</Text>
                
                <Text fw={500}>Response:</Text>
                <Text>{log.response}</Text>
                
                <Group justify="space-between" mt="xs">
                  <Text size="sm">
                    Menu: {log.menu.restaurant.name} - {log.menu.name}
                  </Text>
                  {log.user && (
                    <Text size="sm">User: {log.user.name}</Text>
                  )}
                </Group>
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Container>
    </Layout>
  );
};

export default QueryLogs; 